'use client';
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from 'react';
import { TooltipProvider } from '@sandstone/ui/primitives';
import { VercelToolbarComponent } from './vercel-toolbar';
import { TanStackDevtoolsComponent } from './tanstack-devtools';
import { ReactScan } from './react-scan';
import { ReactGrab } from './react-grab';
import { ZustandDevtools } from './zustand-devtools';
import { DevToolsTogglePanel } from './dev-tools-toggle-panel';
const STORAGE_KEY_REACT_SCAN = 'dev-tools:react-scan-enabled';
const STORAGE_KEY_REACT_GRAB = 'dev-tools:react-grab-enabled';
const STORAGE_KEY_REACT_QUERY = 'dev-tools:react-query-devtools-enabled';
const STORAGE_KEY_ZUSTAND = 'dev-tools:zustand-devtools-enabled';
/**
 * DevToolsProvider - A unified provider for all development tools
 *
 * Bundles Vercel Toolbar, TanStack Devtools, React Scan, and React Grab
 * into a single component. All tools are automatically enabled in development mode.
 *
 * @example
 * ```tsx
 * // app/layout.tsx
 * import { DevToolsProvider } from '@sandstone/dev-tools';
 *
 * export default function RootLayout({ children }: { children: React.ReactNode }) {
 *   return (
 *     <html lang='en'>
 *       <body>
 *         {children}
 *         <DevToolsProvider />
 *       </body>
 *     </html>
 *   );
 * }
 * ```
 *
 * @example
 * ```tsx
 * // Disable specific tools
 * <DevToolsProvider
 *   tools={{
 *     reactScan: false,
 *   }}
 * />
 * ```
 */
export function DevToolsProvider({ enabled, tools, showTogglePanel = true }) {
    const shouldEnable = enabled ?? process.env.NODE_ENV === 'development';
    const [mounted, setMounted] = useState(false);
    // Initialize state from sessionStorage or props
    const getInitialReactScanState = () => {
        if (tools?.reactScan !== undefined) {
            return tools.reactScan;
        }
        if (typeof window !== 'undefined') {
            const stored = sessionStorage.getItem(STORAGE_KEY_REACT_SCAN);
            return stored === 'true';
        }
        return false;
    };
    const getInitialReactGrabState = () => {
        if (tools?.reactGrab !== undefined) {
            return tools.reactGrab;
        }
        if (typeof window !== 'undefined') {
            const stored = sessionStorage.getItem(STORAGE_KEY_REACT_GRAB);
            return stored === 'true';
        }
        return false;
    };
    const getInitialReactQueryDevtoolsState = () => {
        if (tools?.reactQueryDevtools !== undefined) {
            return tools.reactQueryDevtools;
        }
        if (typeof window !== 'undefined') {
            const stored = sessionStorage.getItem(STORAGE_KEY_REACT_QUERY);
            return stored === 'true';
        }
        return false;
    };
    const getInitialZustandDevtoolsState = () => {
        if (tools?.zustandDevtools !== undefined) {
            return tools.zustandDevtools;
        }
        if (typeof window !== 'undefined') {
            const stored = sessionStorage.getItem(STORAGE_KEY_ZUSTAND);
            return stored === 'true';
        }
        return false;
    };
    const [reactScanEnabled, setReactScanEnabled] = useState(getInitialReactScanState);
    const [reactGrabEnabled, setReactGrabEnabled] = useState(getInitialReactGrabState);
    const [reactQueryDevtoolsEnabled, setReactQueryDevtoolsEnabled] = useState(getInitialReactQueryDevtoolsState);
    const [zustandDevtoolsEnabled, setZustandDevtoolsEnabled] = useState(getInitialZustandDevtoolsState);
    useEffect(() => {
        setMounted(true);
    }, []);
    // Memoized toggle handlers to prevent unnecessary re-renders
    const handleReactScanToggle = useCallback((enabled) => {
        setReactScanEnabled(enabled);
    }, []);
    const handleReactGrabToggle = useCallback((enabled) => {
        setReactGrabEnabled(enabled);
    }, []);
    const handleReactQueryDevtoolsToggle = useCallback((enabled) => {
        setReactQueryDevtoolsEnabled(enabled);
    }, []);
    const handleZustandDevtoolsToggle = useCallback((enabled) => {
        setZustandDevtoolsEnabled(enabled);
    }, []);
    // Save to sessionStorage whenever state changes
    useEffect(() => {
        if (typeof window !== 'undefined') {
            sessionStorage.setItem(STORAGE_KEY_REACT_SCAN, String(reactScanEnabled));
        }
    }, [reactScanEnabled]);
    useEffect(() => {
        if (typeof window !== 'undefined') {
            sessionStorage.setItem(STORAGE_KEY_REACT_GRAB, String(reactGrabEnabled));
        }
    }, [reactGrabEnabled]);
    useEffect(() => {
        if (typeof window !== 'undefined') {
            sessionStorage.setItem(STORAGE_KEY_REACT_QUERY, String(reactQueryDevtoolsEnabled));
        }
    }, [reactQueryDevtoolsEnabled]);
    useEffect(() => {
        if (typeof window !== 'undefined') {
            sessionStorage.setItem(STORAGE_KEY_ZUSTAND, String(zustandDevtoolsEnabled));
        }
    }, [zustandDevtoolsEnabled]);
    if (!shouldEnable || !mounted) {
        return null;
    }
    const { vercelToolbar = true, tanStackDevtools = true } = tools ?? {};
    return (_jsxs(_Fragment, { children: [vercelToolbar && _jsx(VercelToolbarComponent, {}), tanStackDevtools && _jsx(TanStackDevtoolsComponent, {}), reactScanEnabled ? _jsx(ReactScan, { enabled: true }, 'react-scan-enabled') : null, reactGrabEnabled ? _jsx(ReactGrab, { enabled: true }, 'react-grab-enabled') : null, zustandDevtoolsEnabled ? _jsx(ZustandDevtools, { enabled: true }, 'zustand-devtools-enabled') : null, showTogglePanel && (_jsx(TooltipProvider, { children: _jsx(DevToolsTogglePanel, { reactScanEnabled: reactScanEnabled, reactGrabEnabled: reactGrabEnabled, reactQueryDevtoolsEnabled: reactQueryDevtoolsEnabled, zustandDevtoolsEnabled: zustandDevtoolsEnabled, onReactScanToggle: handleReactScanToggle, onReactGrabToggle: handleReactGrabToggle, onReactQueryDevtoolsToggle: handleReactQueryDevtoolsToggle, onZustandDevtoolsToggle: handleZustandDevtoolsToggle }) }))] }));
}
